import React, { useState, useMemo } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FaSpinner, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { guestApi } from '../../api/guestList';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../context/AuthContext';
import StyledDataTable from '../../components/StyledDataTable/StyledDataTable';

const BulkImportModal = ({ show, onHide, onImportComplete }) => {
  const { isAuthenticated } = useAuthContext();

  const [fileName, setFileName] = useState("No file chosen");
  const [previewData, setPreviewData] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const columns = [
    { name: '#', selector: row => row.rowNum, sortable: true, width: '70px' },
    { name: 'Name', selector: row => row.name, sortable: true, grow: 2 },
    { name: 'Phone', selector: row => row.phone_number, sortable: true, width: '150px' },
    { name: 'Email', selector: row => row.email, sortable: true, width: '200px' },
    { name: 'Ahadi', selector: row => row.pledge, sortable: true, width: '120px' },
    { name: 'Mchango', selector: row => row.fund, sortable: true, width: '120px' },
    { name: 'Card Type', selector: row => row.CardType, sortable: true, width: '120px' }
  ];

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (e) => {
        const workbook = XLSX.read(e.target.result, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(worksheet);

        const formattedData = data.map((row, index) => ({
          rowNum: index + 1,
          name: row.name || row.jina || row.full_name || '',
          phone_number: row.phone_number || row.namba || row.mobile || '',
          email: row.email || '',
          pledge: row.pledge || row.pledge_amount || row.ahadi || 0,
          fund: row.fund || row.amount_paid || row.mchango || 0,
          CardType: row.CardType || row.card_type || 'Single'
        }));

        setPreviewData(formattedData);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleBulkImport = async () => {
    if (previewData.length === 0) {
      toast.warning('No data to import');
      return;
    }

    setSubmitting(true);
    try {
      const formattedData = previewData.map(item => ({
        name: String(item.name || '').trim(),
        phone_number: String(item.phone_number || '').trim(),
        email: item.email || null,
        pledge: parseFloat(item.pledge) || 0,
        fund: parseFloat(item.fund) || 0,
        card_type: item.CardType || 'Single'
      }));

      const response = await guestApi.uploadContacts(formattedData);

      let successful = [];
      let duplicates = [];
      let errors = [];

      const responseData = response?.data || response || [];

      if (Array.isArray(responseData)) {
        responseData.forEach((itemArray) => {
          if (itemArray && itemArray.length > 0) {
            const item = itemArray[0];
            if (item.status === '1') {
              successful.push(item);
            } else if (item.status === '0' && item.details === 'Duplicate') {
              duplicates.push(item);
            } else if (item.status === '0') {
              errors.push({ ...item, error: item.details || 'Unknown error' });
            }
          }
        });
      }

      onImportComplete({ success: successful, skipped: duplicates, errors: errors });

      // Clear state
      setPreviewData([]);
      setFileName("No file chosen");
      onHide();

    } catch (error) {
      console.error("Bulk import error:", error);

      if (error.response?.data?.data && Array.isArray(error.response.data.data)) {
        const errorData = error.response.data.data;
        let successful = [];
        let duplicates = [];
        let errors = [];

        errorData.forEach((itemArray) => {
          if (itemArray && itemArray.length > 0) {
            const item = itemArray[0];
            if (item.status === '1') {
              successful.push(item);
            } else if (item.status === '0' && item.details === 'Duplicate') {
              duplicates.push(item);
            } else if (item.status === '0') {
              errors.push({ ...item, error: item.details || 'Import failed' });
            }
          }
        });

        if (successful.length > 0 || duplicates.length > 0 || errors.length > 0) {
          onImportComplete({ success: successful, skipped: duplicates, errors });
          setPreviewData([]);
          setFileName("No file chosen");
          onHide();
          return;
        }
      }

      toast.error(`Import failed: ${error.response?.data?.message || error.message || "An error occurred during import"}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadExcelTemplate = () => {
    const templateData = [
      {
        name: 'Mr and Mrs Joshy',
        phone_number: '255678345678',
        pledge: '100000',
        fund: '50000',
        CardType: 'Single/Double'
      }
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(templateData);
    ws['!cols'] = [{ wch: 30 }, { wch: 20 }, { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, ws, "Guest Template");
    XLSX.writeFile(wb, "guest_import_template.xlsx");
    toast.info('Template downloaded successfully');
  };




  if (!isAuthenticated) return null;

  return (
    <Modal show={show} onHide={onHide} size={previewData.length > 0 ? "xl" : "lg"}>
      <Modal.Header closeButton>
        <Modal.Title>Import Wageni Kutoka Excel</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-4">
          <p>
            Pakua template hapa chini, ijaze, kisha ipandishe hapa. Hakikisha majina ya column yanafanana na ya template.
          </p>
          <Button variant="outline-primary" size="sm" onClick={handleDownloadExcelTemplate}>
            Download Template
          </Button>
        </div>

        <div className="file-upload-container p-4 border rounded bg-light text-center mb-4">
          <input
            type="file"
            id="excel-upload"
            accept=".xlsx, .xls"
            onChange={handleFileUpload}
            className="d-none"
          />
          <label htmlFor="excel-upload" className="btn btn-primary mb-2 cursor-pointer">
            Chagua File (.xlsx)
          </label>
          <div><small className="text-muted">{fileName}</small></div>
        </div>

        {previewData.length > 0 && (
          <div className="border rounded bg-white overflow-hidden">
            <StyledDataTable
              columns={columns}
              data={previewData}
              pagination
              paginationPerPage={20}
              paginationRowsPerPageOptions={[20, 50, 100, 200]}
              noHeader
              responsive={false}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Funga
        </Button>
        <Button
          variant="success"
          onClick={handleBulkImport}
          disabled={submitting || previewData.length === 0}
        >
          {submitting ? <FaSpinner className="fa-spin" /> : `Import Data (${previewData.length})`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkImportModal;
